import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { type AppConfig, getConfig } from '../config';   // your existing config.ts

interface ConfigContextType {
  config: AppConfig;
  loading: boolean;
  error: Error | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initConfig = async () => {
      try {
        // Ensure config is loaded
        if (!window.ENV) {
          // You can call loadConfig() here if needed
          console.warn("window.ENV not found - make sure main.tsx loaded it");
        }
        const cfg = getConfig();
        setConfig(cfg);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load config'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    initConfig();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading configuration...</div>;
  }

  if (error || !config) {
    return <div className="text-red-500 p-4">Failed to load application configuration.</div>;
  }

  return (
    <ConfigContext.Provider value={{ config, loading: false, error: null }}>
      {children}
    </ConfigContext.Provider>
  );
}

// Custom Hook - Super easy to use
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
};